import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import {
  determineAssetType,
  editInterfaceUrl,
  getAgentsOrigin,
  getAssetId,
  getTablesOrigin,
} from "../utils";
import { AssetType, DefaultNodeData, NodeType } from "../types";
import { nodeAtomFamily } from "./nodeAtomFamily";

type Params = {
  assetType: AssetType;
  assetId: string | null;
  interfaceProjectId: string | undefined;
};

function getAssetLink({ assetType, assetId, interfaceProjectId }: Params) {
  if (assetType === "zap" && assetId) {
    return `/editor/${assetId}`;
  }

  if (assetType === "table" && assetId) {
    return `//${getTablesOrigin()}/app/tables/t/${assetId}`;
  }

  if (assetType === "chatbot" && assetId) {
    return `/app/chatbots/${assetId}`;
  }

  if (assetType === "interface" && (interfaceProjectId || assetId)) {
    return editInterfaceUrl(interfaceProjectId!, assetId);
  }

  if (assetType === "agent" && assetId) {
    return `${getAgentsOrigin()}/bots/${assetId}/settings`;
  }
}

export const assetTypeAndLinkAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom((get) => {
    const node = get(nodeAtomFamily(nodeId));

    if (!node) {
      return {
        assetType: undefined,
        assetLink: undefined,
      };
    }

    const { data, type } = node;

    const assetType = determineAssetType(
      (data as DefaultNodeData).services,
      type as NodeType,
    );

    const assetId = getAssetId(data, assetType);
    const interfaceProjectId = (data as DefaultNodeData).projectId;

    const assetLink = getAssetLink({
      assetType,
      assetId,
      interfaceProjectId,
    });

    return {
      assetType,
      assetLink,
    };
  });

  baseAtom.debugLabel = `assetTypeAndLinkAtomFamily(${nodeId})`;
  return baseAtom;
});
